import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {CrossIcon} from '../../icons/cross-icon/cross_icon';
import {Button, VARIANT} from '../button';
import {
	StyledContainer,
	StyledBulkSelectPopup,
	StyledInfo,
	StyledItemCountWrapper,
	StyledCounterSpacing,
	StyledCounter,
	StyledCounterText,
	StyledButtonGroupContent,
	StyledDropdown,
	StyledButtonGroup,
	StyledListSection,
	StyledItem,
	FlexColumn,
	OptionsRow,
	CrossIconContainer,
	WarningContainer,
	WarningIconContainer,
} from './bulk_select_popup.styled';

export const BulkSelectPopup = ({
	onClose,
	itemCount,
	counterText,
	actionOptions,
	complexOptions,
	warningMessage,
	warningIcon,
}) => {
	const [selectedButtonId, setSelectedButtonId] = useState(null);

	// for registering clicks outside of button group
	// a button group is a button and associated dropdown
	const buttonGroupRef = useRef();

	/**
	 * takes a function and calls it, if its not undefined
	 * @param {Function} callback
	 */
	const handleClick = callback => {
		if (callback) {
			setSelectedButtonId(null);
			callback();
		}
	};

	/**
	 * Sets selected button. and calls the callback function
	 * @param {object} button
	 */
	const handleButtonClick = button => {
		setSelectedButtonId(selectedButtonId !== button.id ? button.id : null);
		handleClick(button.callback);
	};

	/**
	 * return whether id is equal to selectedButtonId
	 * @param {string} id
	 * @returns {boolean}
	 */
	const isButtonSelected = id => {
		return id === selectedButtonId;
	};

	/**
	 * disables selectedButton if event is outside buttonGroupRef
	 * @param {MouseEvent} event
	 */
	const handleClickOutside = event => {
		if (buttonGroupRef.current && !buttonGroupRef.current.contains(event.target)) {
			setSelectedButtonId(null);
		}
	};

	/**
	 * Adds/ removes a Listener to mousedown event
	 */
	useEffect(() => {
		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};
	});

	/**
	 * Recursive function calling itself with options until options do not exist
	 * @param {object} options
	 * @param {number} count default 1
	 */
	const listOptions = (options, count = 1) => {
		let result = [];

		result = options.map((option, i) =>
			option.section ? (
				<StyledListSection key={i + count}>
					<StyledItem
						disabled={option.disabled}
						onClick={() => !option.disabled && handleClick(option.callback)}
						indent={count}
						isTitle={option.options}
					>
						{option.label}
					</StyledItem>
					{option.options && listOptions(option.options, count + 1)}
				</StyledListSection>
			) : (
				<React.Fragment key={i + count}>
					<StyledItem
						disabled={option.disabled}
						onClick={() => !option.disabled && handleClick(option.callback)}
						indent={count}
						isTitle={option.options}
					>
						{option.label}
					</StyledItem>
					{option.options && listOptions(option.options, count + 1)}
				</React.Fragment>
			)
		);
		return result;
	};

	return (
		<StyledContainer>
			<StyledBulkSelectPopup className={'animated faster fadeInUp'} data-userpilot={'bulk-select-popup'}>
				<CrossIconContainer>
					<CrossIcon onClick={onClose} />
				</CrossIconContainer>
				<FlexColumn>
					<OptionsRow>
						<StyledInfo>
							<StyledItemCountWrapper>
								<StyledCounterSpacing>
									<StyledCounter data-cy="bulk-popup-counter">{itemCount}</StyledCounter>
								</StyledCounterSpacing>
								<StyledCounterText>{counterText}</StyledCounterText>
							</StyledItemCountWrapper>
						</StyledInfo>

						{complexOptions &&
							complexOptions.map(option => (
								<StyledButtonGroup
									key={option.id}
									ref={isButtonSelected(option.id) ? buttonGroupRef : null}
									onClick={() => handleButtonClick(option)}
								>
									<StyledButtonGroupContent>
										{isButtonSelected(option.id) && option.options && (
											<StyledDropdown>{listOptions(option.options)}</StyledDropdown>
										)}
										{option.renderButton(isButtonSelected(option.id))}
									</StyledButtonGroupContent>
								</StyledButtonGroup>
							))}

						{actionOptions &&
							actionOptions.map(option => (
								<StyledButtonGroup key={option.id} ref={isButtonSelected(option.id) ? buttonGroupRef : null}>
									<StyledButtonGroupContent>
										{isButtonSelected(option.id) && option.options && (
											<StyledDropdown>{listOptions(option.options)}</StyledDropdown>
										)}
										<Button
											cy={option.cy}
											disabled={option.disabled}
											onClick={() => handleButtonClick(option)}
											iconPosition={option.icon ? 'first' : undefined}
											icon={option.icon}
											userpilot={option.userpilot}
											variant={isButtonSelected(option.id) ? VARIANT.PURPLE_FILLED : option.variant}
										>
											{option.label}
										</Button>
									</StyledButtonGroupContent>
								</StyledButtonGroup>
							))}
					</OptionsRow>
					{warningMessage && (
						<WarningContainer>
							<div>{warningMessage}</div>
							{warningIcon && <WarningIconContainer>{warningIcon}</WarningIconContainer>}
						</WarningContainer>
					)}
				</FlexColumn>
			</StyledBulkSelectPopup>
		</StyledContainer>
	);
};

BulkSelectPopup.propTypes = {
	/**
	 * bulk select popup onClose function
	 */
	onClose: PropTypes.func.isRequired,
	/**
	 * a counter of how many items has been selected for bulk action
	 */
	itemCount: PropTypes.number,
	/**
	 * a name for the items selected
	 */
	counterText: PropTypes.string,
	/**
	 * options of bulk actions that can be done to the selected items
	 */
	actionOptions: PropTypes.arrayOf(PropTypes.object),
	/**
	 * complex options of bulk actions that like actionOptions can be done to the selected items but require custom button group content
	 */
	complexOptions: PropTypes.arrayOf(PropTypes.object),
	/**
	 * warning text shown at the bottom of the popup
	 */
	warningMessage: PropTypes.arrayOf(PropTypes.string),
	/**
	 * warning icon
	 */
	warningIcon: PropTypes.arrayOf(PropTypes.element),
};

BulkSelectPopup.defaultProps = {
	actionOptions: [],
	complexOptions: [],
	counterText: 'items selected',
};
